import React from 'react';
import { useNavigate } from 'react-router-dom';
import navigationData from '../../data/navigation.json';
import logo from '../../assets/images/logo100.png';
import { FooterSection, SocialMediaItem, SubNavItem } from '../../types';
import './Footer.css';

// Define interface for the link item in footer
interface FooterLink {
  url: string;
  title: {
    ar: string;
    en: string;
  };
}

const Footer: React.FC = () => {
  const navigate = useNavigate();
  const navData = navigationData as any; // Type assertion since JSON import

  // Get footer sections
  const quickLinksSection = navData.footerSections.find(
    (section: FooterSection) => section.id === 'quick-links'
  );

  // Get program items from main nav for footer
  const programsItems = navData.mainNav.find(
    (item: any) => item.id === 'programs'
  )?.items || [];

  // Handle shop button click with scroll to top
  const handleShopClick = (e: React.MouseEvent) => {
    e.preventDefault(); // Prevent default link behavior

    // Navigate to shop page
    navigate('/shop');

    // Scroll to top of the page
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  // Render social media icons
  const renderSocialIcons = () => {
    return navData.socialMedia.map((platform: SocialMediaItem) => {
      let svgHTML = '';

      // Add appropriate SVG for each social media platform
      switch(platform.icon) {
        case 'linkedin':
          svgHTML = '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.454C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.225 0z"/></svg>';
          break;
        case 'facebook':
          svgHTML = '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>';
          break;
        case 'instagram':
          svgHTML = '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/></svg>';
          break;
        case 'whatsapp':
          svgHTML = '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>';
          break;
        default:
          svgHTML = '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10"/></svg>';
      }

      return (
        <a
          key={platform.title}
          href={platform.url}
          target="_blank"
          rel="noopener noreferrer"
          className="social-icon"
          aria-label={platform.title}
          dangerouslySetInnerHTML={{ __html: svgHTML }}
        />
      );
    });
  };

  return (
    <footer>
      <div className="footer-container">
        <div className="footer-logo">
          <img src={logo} alt="AI Academy Logo" />
          <p className="ar-content">تشكيل مبتكري الغد وتمكين محترفي اليوم</p>
          <p className="en-content">Shaping tomorrow's innovators and empowering today's professionals.</p>
          <div className="social-icons">
            {renderSocialIcons()}
          </div>
        </div>

        {quickLinksSection && (
          <div className="footer-links">
            <h3 className="ar-content">{quickLinksSection.title.ar}</h3>
            <h3 className="en-content">{quickLinksSection.title.en}</h3>
            <ul>
              {quickLinksSection.links.map((link: FooterLink, index: number) => (
                <li key={`quick-link-${index}`}>
                  {/* KEY CHANGE HERE: Always use absolute paths for links */}
                  <a
                    href={link.url.startsWith('#') ? '/' + link.url : link.url}
                    className="ar-content"
                  >
                    {link.title.ar}
                  </a>
                  <a
                    href={link.url.startsWith('#') ? '/' + link.url : link.url}
                    className="en-content"
                  >
                    {link.title.en}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        )}

        <div className="footer-links">
          <h3 className="ar-content">البرامج</h3>
          <h3 className="en-content">Programs</h3>
          <ul>
            {programsItems.map((program: SubNavItem, index: number) => (
              <li key={`program-${index}`}>
                {/* KEY CHANGE HERE: Always use absolute paths for links */}
                <a
                  href={program.url.startsWith('#') ? '/' + program.url : program.url}
                  className="ar-content"
                >
                  {program.title.ar}
                </a>
                <a
                  href={program.url.startsWith('#') ? '/' + program.url : program.url}
                  className="en-content"
                >
                  {program.title.en}
                </a>
              </li>
            ))}
          </ul>
        </div>

        {/* New Shop Featured Link Section with onClick handler */}
        <div className="footer-shop-promo">
          <h3 className="ar-content">متجر الأكاديمية</h3>
          <h3 className="en-content">Academy Shop</h3>
          <p className="shop-promo-text ar-content">
            اكتشف مجموعتنا من الأدوات العلمية والتعليمية المميزة التي تم اختيارها بعناية لتعزيز التعلم في العلوم والتكنولوجيا والهندسة والرياضيات
          </p>
          <p className="shop-promo-text en-content">
            Discover our collection of premium educational products carefully curated to enhance learning in science, technology, engineering, and mathematics
          </p>
          <a href="/shop" className="shop-promo-button" onClick={handleShopClick}>
            <span className="ar-content">زيارة المتجر</span>
            <span className="en-content">Visit Shop</span>
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="18" height="18" fill="currentColor" className="shop-icon">
              <path d="M7 18c-1.1 0-1.99.9-1.99 2S5.9 22 7 22s2-.9 2-2-.9-2-2-2zM1 2v2h2l3.6 7.59-1.35 2.45c-.16.28-.25.61-.25.96 0 1.1.9 2 2 2h12v-2H7.42c-.14 0-.25-.11-.25-.25l.03-.12.9-1.63h7.45c.75 0 1.41-.41 1.75-1.03l3.58-6.49c.08-.14.12-.31.12-.48 0-.55-.45-1-1-1H5.21l-.94-2H1zm16 16c-1.1 0-1.99.9-1.99 2s.89 2 1.99 2 2-.9 2-2-.9-2-2-2z"/>
            </svg>
          </a>
        </div>
      </div>


      <div className="footer-bottom">
        <p className="ar-content">© 2025 أكاديمية الذكاء الاصطناعي. جميع الحقوق محفوظة.</p>
        <p className="en-content">&copy; 2025 AI Academy. All Rights Reserved.</p>
      </div>
    </footer>
  );
};

export default Footer;